<template>
  <v-card>
    <v-form ref="form" :lazy-validation="true">
      <v-card-title>Maskinslag</v-card-title>
      <v-card-actions>
        <v-icon class="close" @click="$emit('close')">$sl-close</v-icon>
      </v-card-actions>

      <!-- Show machine qualification starts here -->
      <v-card-text class="mt-n6" v-if="mode === 'show'">
        <h6 class="mt-n3 pb-4 text-uppercase">{{ mq.machine ? mq.machine.name : '-' }}</h6>
        <p class="overline">Utfärdat</p>
        <p class="mt-n3">{{ mq.issuedOn | date }}</p>
        <p class="overline">Utfärdat av</p>
        <p class="mt-n3">{{ mq.issuedById ? mq.issuedBy.firstName + ' ' + mq.issuedBy.lastName : '-' }}</p>
        <p class="overline">Region</p>
        <p class="mt-n3">{{ mq.region ? mq.region.name : '-' }}</p>
        <template v-if="mq.approvedOn">
          <p class="overline">Godkänt</p>
          <p class="mt-n3">{{ mq.approvedOn | date }}</p>
          <p class="overline">Godkänt av</p>
          <p class="mt-n3">{{ mq.approvedBy ? mq.approvedBy.firstName + ' ' + mq.approvedBy.lastName : '-' }}</p>
        </template>
        <template v-else>
          <h6 class="mt-10">Ännu ej godkänt</h6>
        </template>
      </v-card-text>
      <!-- Show machine qualification ends here -->

      <!-- Approve machine qualification starts here -->
      <v-card-text v-if="mode === 'approve'">
        <h6 class="mt-n6 mb-8">Vill du godkänna maskinslag?</h6>
        <h6 class="pb-4 text-uppercase">{{ mq.machine ? mq.machine.name : '-' }}</h6>
        <p class="overline">Utfärdat</p>
        <p class="mt-n3">{{ mq.issuedOn | date }}</p>
        <p class="overline">Utfärdat av</p>
        <p class="mt-n3">{{ mq.issuedById ? mq.issuedBy.firstName + ' ' + mq.issuedBy.lastName : '-' }}</p>
        <p class="overline">Region</p>
        <p class="mt-n3">{{ mq.region ? mq.region.name : '-' }}</p>
      </v-card-text>
      <!-- Approve machine qualification ends here -->
      <!-- Create new machine qualification starts here-->
      <v-card-text>
        <template v-if="mode === 'create'">
          <v-select
            outlined
            v-if="basicMachines"
            v-model="createData.machineId"
            :items="activeMachines.map(o=>{return {id: o.id, text: o.name}})"
            item-text="text"
            item-value="id"
            label="Maskin"
            :rules="[rules.required]"
          ></v-select>
          <v-select
            outlined
            v-model="createData.regionId"
            :items="selectableRegions"
            item-text="text"
            item-value="id"
            label="Region"
            :rules="[rules.required]"
          ></v-select>

          <DatePicker
            v-model="createData.issuedOn"
            ref="datePicker"
            :icon="'$sl-calendar-date'"
            :label="'Utfärdandedatum'"
            :width="'250'"
            outlined
          />
        </template>
      </v-card-text>

      <v-card-actions class="justify-end mt-n6">
         <v-btn color="secondary" @click="$emit('close')">
          <v-icon>$sl-cancel</v-icon>Avbryt
        </v-btn>
        <v-btn v-if="userAdminAndKanslistCan()" outlined @click="deleteMachineQualification(mq)">
          <v-icon>$sl-delete</v-icon>Radera
        </v-btn>
        <v-btn v-if="mode === 'create'" @click="emitCreateData" :disabled="!canCreate">
          <v-icon>$sl-check</v-icon>Utfärda
        </v-btn>
        <v-btn v-if="mode === 'approve'" @click="$emit('approve', mq)" :disabled="!canApprove">
          <v-icon>$sl-like</v-icon>Godkänn
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import DatePicker from '@/components/common/DatePicker'
import { Regions, RulesMixin, UserRules, CanIDo } from "@/components/mixins";
import API from "@/services/api";

// @group Person
// PersonMachineQualificationModal component
export default {
  name: 'PersonMachineQualificationModal',
  mixins: [Regions, RulesMixin, UserRules, CanIDo],
  components: {
    DatePicker
  },
  data: () => ({
    createData: {personId: null, machineId: null, regionId: null, issuedOn: null, issuedById: null}
  }),

  props: {
    //has machine qualification object
    mq: Object,
    // modal mode string
    mode: String
  },
  computed: {
    ...mapGetters('person', ['selectedPerson']),
    ...mapGetters('currentuser', ['currentUser', 'currentUserRole']),
    ...mapGetters('basic', ['activeMachines','basicMachines', 'getMachineNameById', 'basicRegions'])
  },

  asyncComputed: {
    canApprove(){
      return this.canIDo('person-qualification', 'update');
    },
    canCreate(){
      return this.canIDo('person-qualification', 'create');
    },
  },

  methods: {
    ...mapActions("person", ["setSelectedPerson"]),
    emitCreateData() {
      if (
        !this.$refs.form.validate() ||
        !this.$refs.datePicker.$refs.form.validate() ||
        (this.createData &&
          (this.createData.machineId == null || this.createData.issuedOn == null || this.createData.regionId == null))
      ) {
        return this.$events.fire('showError', 'Formuläret är inte korrekt ifyllt.')
      }
      this.$emit('create', this.createData)
    },

    async deleteMachineQualification(mq) {
      const res = await this.$dialog.confirm({
        text: "Radering av maskinbehörigheter går inte att ångra.",
        title: `Vill du radera behörighet för ${this.selectedPerson.firstName} ${this.selectedPerson.lastName}?`,
        actions: [
          { text: "Radera", icon: "$sl-delete", key: true },
          { text: "Avbryt", key: false, icon: "$sl-cancel", outlined: true },
        ],
      });
      if (res !== true || (!mq && !mq.id)) return;

      API.deleteMachineQualification(mq.id)
        .then((res) => {
          API.getPerson(this.selectedPerson.id)
            .then((response) => {
              /**
               * @vuese
               * Used to update local store with the person data
               * @arg the argument is an object representing person
               */
              this.setSelectedPerson(response.data);
              this.$events.fire("showInfo", "Raderad.");
              this.$emit("close");
            })
            .catch((error) => {
              console.log("There was an error:", error.response);
            });
        })
        .catch((error) => {
          console.log(error);
        });
    },
  }
}
</script>

<style>
</style>
